import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CORE_TIMEZONES,
  formatInTimezone,
  getOrganizationTimezones,
  listLocationTimezones,
  setLocationTimezone,
  setOrganizationTimezone,
  setTenantTimezone,
  setUserTimezone,
  timezoneMeta,
  type CoreLocationTimezone,
} from '@/lib/coreSystem';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const SELECT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors cursor-pointer';

export default function TimezoneTab({ org }: Props) {
  const { user } = useAuth();
  const [tenantTz, setTenantTz] = useState<string>('UTC');
  const [orgTz, setOrgTz] = useState<string>('UTC');
  const [locations, setLocations] = useState<CoreLocationTimezone[]>([]);
  const [userTz, setUserTz] = useState<string>('UTC');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tz = await getOrganizationTimezones(org.id);
      setTenantTz(tz.tenant_timezone || 'UTC');
      setOrgTz(tz.organization_timezone || tz.tenant_timezone || 'UTC');
      setLocations(await listLocationTimezones(org.id));
      if (user?.id) {
        const { data } = await supabase.from('users').select('timezone').eq('id', user.id).maybeSingle();
        setUserTz((data as { timezone?: string } | null)?.timezone || orgTz || 'UTC');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, user?.id, orgTz]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const effectiveQrCode = useMemo(() => orgTz || tenantTz || 'UTC', [orgTz, tenantTz]);

  const save = async (key: string, fn: () => Promise<void>) => {
    setSaving(key);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); } finally { setSaving(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <p className="text-xs text-foreground-500 max-w-2xl">
        Les dates sont stockées de façon cohérente puis <span className="font-semibold text-foreground-700">affichées selon le fuseau approprié</span>.
        Essentiel pour les rendez-vous, réservations, hôtels, activités, événements et notifications.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Société */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-building-2-line"></i></span>
            <div>
              <h4 className="text-sm font-semibold text-foreground-900">Fuseau de l&apos;entreprise</h4>
              <p className="text-[11px] text-foreground-400">Par défaut pour toute l&apos;organisation</p>
            </div>
          </div>
          <select
            className={SELECT}
            value={orgTz}
            disabled={saving === 'org'}
            onChange={(e) => { setOrgTz(e.target.value); save('org', () => setOrganizationTimezone(org.id, e.target.value)); }}
          >
            {CORE_TIMEZONES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-time-line text-primary-500"></i>
            Heure locale : <span className="font-semibold text-foreground-800 tabular-nums">{formatInTimezone(now, orgTz)}</span>
          </div>
        </div>

        {/* Tenant */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-global-line"></i></span>
            <div>
              <h4 className="text-sm font-semibold text-foreground-900">Fuseau du compte (tenant)</h4>
              <p className="text-[11px] text-foreground-400">Niveau le plus global</p>
            </div>
          </div>
          <select
            className={SELECT}
            value={tenantTz}
            disabled={saving === 'tenant' || !org.tenant_id}
            onChange={(e) => { setTenantTz(e.target.value); if (org.tenant_id) save('tenant', () => setTenantTimezone(org.tenant_id as number, e.target.value)); }}
          >
            {CORE_TIMEZONES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-time-line text-secondary-500"></i>
            Heure locale : <span className="font-semibold text-foreground-800 tabular-nums">{formatInTimezone(now, tenantTz)}</span>
          </div>
        </div>
      </div>

      {/* Utilisateur */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-user-3-line"></i></span>
          <div>
            <h4 className="text-sm font-semibold text-foreground-900">Mon fuseau horaire</h4>
            <p className="text-[11px] text-foreground-400">Appliqué à votre affichage personnel</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="sm:max-w-xs w-full">
            <select
              className={SELECT}
              value={userTz}
              disabled={saving === 'user' || !user?.id}
              onChange={(e) => { setUserTz(e.target.value); if (user?.id) save('user', () => setUserTimezone(user.id, e.target.value)); }}
            >
              {CORE_TIMEZONES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-time-line text-accent-500"></i>
            Heure actuelle : <span className="font-semibold text-foreground-800 tabular-nums">{formatInTimezone(now, userTz)}</span>
          </div>
        </div>
      </div>

      {/* Établissements */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center"><i className="ri-store-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Fuseaux par établissement</h4>
        </div>
        {locations.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun établissement enregistré.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {locations.map((loc) => (
              <div key={loc.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0 sm:w-48">
                  <i className="ri-map-pin-2-line text-foreground-400"></i>
                  <span className="text-sm font-medium text-foreground-800 truncate">{loc.name}</span>
                  {loc.is_primary && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">Principal</span>}
                </div>
                <div className="flex-1">
                  <select
                    className={SELECT}
                    value={loc.timezone || effectiveQrCode}
                    disabled={saving === `loc-${loc.id}`}
                    onChange={(e) => save(`loc-${loc.id}`, () => setLocationTimezone(loc.id, e.target.value))}
                  >
                    {CORE_TIMEZONES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </select>
                </div>
                <span className="text-xs text-foreground-500 tabular-nums whitespace-nowrap sm:w-40 sm:text-right">
                  {formatInTimezone(now, loc.timezone || effectiveQrCode, { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 bg-secondary-50 border border-secondary-200 rounded-lg flex items-start gap-3">
        <i className="ri-information-line text-secondary-700 text-lg mt-0.5"></i>
        <p className="text-xs text-secondary-900">
          Priorité d&apos;affichage : <span className="font-semibold">établissement</span> → <span className="font-semibold">entreprise</span> → <span className="font-semibold">compte</span>.
          Le fuseau de {timezoneMeta(effectiveQrCode).label} sert de repli pour les modules sans préférence.
        </p>
      </div>
    </div>
  );
}